import { CheckCircleIcon, PhotoIcon, XMarkIcon } from '@heroicons/react/20/solid';
import cleanDeep from 'clean-deep';
import { addYears, differenceInWeeks, format, parse, subYears } from 'date-fns';
import React, { useEffect } from 'react';
import ReactDatePicker from 'react-datepicker';
import ReactFileReader from 'react-file-reader';
import { connect } from 'react-redux';
import ReactSelect from 'react-select';
import Tooltip from '@/tooltip';
import { Actions as EMPLOYEE_ACTIONS } from '../../../store/actions/employeeActions';
import { Actions as LEAVE_ACTIONS } from '../../../store/actions/leaveActions';
import { DateInputFormat, handleRawDate } from '../../common/DateComponents';
import { ImagePlusIcon } from '../../common/icons/Solid';
import Spinner from '../../common/Spinner';
import LeaveBreakdown from '../../leave/leave-breakdown/LeaveBreakdown';

const stringToDate = (dateString) => {
    if (!dateString) return new Date()
    return parse(dateString, 'yyyy-MM-dd', new Date())
}

const dateToString = (date) => {
    if (!date) return undefined
    return format(date, 'yyyy-MM-dd')
}


const ChildLeaveModal = ({
    leave,
    closeModal,
    modalOpen,
    saveItem,
    saving,
    disabled,
    leaveTypeOptions,
    leaveBalance,
    employeeName,
    child,
    leaveBalancesUpdating,
    agreementId,
    updateField,
    leaveStatusOptions,
    getChildRelatedBalance,
    getLeaveTypes,
    uploading,
    uploaded,
    getLeaveBreakdown,
    mapBreakdown,
    lastPaidDate,
    modalIsPaid,
    jurisdiction,
    uploadFile
}) => {
    const [minDate, maxDate] = [subYears(new Date(), 100), addYears(new Date(), 10)]
    const { reference: childName, id: childId } = { ...child }

    const handleFileUpload = (file) => {
        uploadFile(file[0], agreementId);
    }
    // const updateField = (field, value) => {
    //     if (field.includes('[')) field = _.setWith({}, field, value, Object)
    //     const newleave = _.merge(leave, { ...(typeof field == 'object' ? field : { [field]: value }) })
    //     setleave({ ...newleave })
    // }

    const { applyIfBalanceAvailable } = { ...(leaveTypeOptions?.find(item => item?.value == leave?.type)) }
    const { canHaveMultipleEntries, consumeBy, childDOB, entitledLatest, entitledTotal, revokeAfter, notifiedBefore, entitledPaid } = { ...leaveBalance }
    const isPaid = leave?.date != leave?.dateTo ? !!lastPaidDate : modalIsPaid

    useEffect(() => {
        getLeaveTypes()
    }, [])

    useEffect(() => {
        if (leave.type && childId && agreementId) getChildRelatedBalance({ type: leave.type, childId, agreementId, date: canHaveMultipleEntries ? leave.date : undefined })
    }, [leave.type, canHaveMultipleEntries ? leave.date : undefined])

    useEffect(() => {
        // console.debug({ date: !!leave.date, dateTo: !!leave.dateTo, dateNotified: !!(notifiedBefore ? leave.dateNotified : true) })
        if (leave.date && leave.dateTo && (notifiedBefore ? leave.dateNotified : true) && !leave.isParentEntry) {
            // console.debug('mapped breakdown')
            mapBreakdown()
        }
    }, [leave.date, leave.dateTo, leave.dateNotified, leave.dateRevoked, leave?.id])

    useEffect(() => {
        if (leave?.id && (leave?.date !== leave?.dateTo) ) {
            getLeaveBreakdown(leave.id)
            updateField('isParentEntry')
        }
    }, [leave.id])

    useEffect(() => {
        if (!modalOpen || saving) return
        const exists = Object.keys(cleanDeep({ ...leave }))?.length > 1
        if (!exists) {
            updateField('type', leave?.type ? leave.type : leaveTypeOptions[0]?.value)
            updateField('agreement', agreementId)
            updateField('childId', childId)
            updateField('status', leave?.status ? leave.status : leaveStatusOptions[0]?.value)
        }
    }, [leave, modalOpen])

    if (!modalOpen || ["MT"].includes(jurisdiction)) return null

    const SubmitButtons = () => {
        const hasRequired = leave.date && leave.dateTo && (notifiedBefore ? leave.dateNotified : true)
        const hasAttachment = uploaded?.length
        if (!hasRequired) return <button className="btn btn-default pull-right" style={{ marginRight: 0 }}>Leave Application Incomplete</button>

        return (
            <button
                className={`btn btn-${hasAttachment ? 'success' : 'warning'} pull-right`}
                disabled={saving || disabled}
                onClick={() => { if (!saving) submit() }}
            >
                {saving ? <>
                    <Spinner
                        style={{ width: '1.5rem', height: '1.5rem', marginRight: 5 }}
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                    <div style={{ float: "left" }}>Saving...</div>
                </> : <>
                    <CheckCircleIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
                    <div style={{ float: "left" }}>{leave.id ? 'Update' : 'Submit'}{!hasAttachment ? ' without Attachment' : null}</div>
                </>}
            </button>
        )
    }

    const submit = () => {
        saveItem(leave)
    }

    return (
        <div className="modal" tabIndex="-1" role="dialog" aria-hidden="true" style={{ display: "block", background: "rgba(0,0,0,0.4)", overflow: "auto" }}>
            <div className="modal-dialog" style={{ width: "60%" }}>
                <div className="modal-content">
                    <Tooltip
                        direction={'left'}
                        distance={20}
                        isOpen={isPaid ? true : false}
                        content={<div style={{ paddingLeft: '2rem' }}>
                            <h5 style={{ textAlign: 'left' }}>This leave has already been paid</h5>
                            <p style={{ textAlign: 'left', color: 'white', fontSize: '1.25rem' }}>To edit this leave entry, you must edit the linked payslip from the corresponding payroll.</p>
                            <h6 style={{ textAlign: 'left' }}>You may only edit:</h6>
                            <ul style={{ listStyle: 'disc', float: 'left', paddingLeft: '2.5rem', textAlign: 'left' }}>
                                <li>Leave days not yet paid in payslip</li>
                                <li>Comments</li>
                                <li>Attachments</li>
                            </ul>
                            <br />
                            <a
                                style={{
                                    fontSize: 11,
                                    padding: "3px 6px",
                                    paddingLeft: 0,
                                    textDecoration: 'underline',
                                    float: 'left',
                                    color: "white",
                                    cursor: "pointer"
                                }}
                                target="_blank"
                                href="https://support.buddy.hr/hc/en-mt/articles/7078759037970-Editing-an-Entry-that-is-Marked-as-Paid"
                            >
                                Editing an Entry that is Marked As Paid
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                            </a>
                        </div>}
                    >
                        <div className="block block-themed block-transparent remove-margin-b" style={{ padding: 0 }}>
                            <div className="block-header bg-primary-dark" style={{ padding: 20, position: 'sticky', top: 0, zIndex: 50 }}>
                                <ul className="block-options">
                                    <li>
                                        <button type="button" onClick={closeModal}><XMarkIcon className="h-6 w-6 mr-3 text-white" /></button>
                                    </li>
                                </ul>
                                <h3 className="block-title">{leave?.id ? `Edit` : 'Add'} Leave For {employeeName}</h3>
                            </div>
                            <div className="block-content">
                                <div className='col-xs-12 form-container'>
                                    <div className="col-xs-6">
                                        <label htmlFor="material-text">Child</label>
                                        <ReactSelect
                                            value={{ label: childName }}
                                            isDisabled={true}
                                        />
                                    </div>
                                    <div className="col-xs-6">
                                        <label htmlFor="material-text">Type</label>
                                        <ReactSelect
                                            value={leaveTypeOptions?.find(item => item.value == leave.type)}
                                            options={leaveTypeOptions}
                                            onChange={(selected) => {
                                                updateField('type', selected?.value)
                                                updateField('date')
                                                updateField('dateTo')
                                                updateField('dateNotified')
                                                updateField('dateRevoked')
                                                updateField('weeklyBreakdown')
                                                updateField('hoursBreakdown')
                                            }}
                                            isDisabled={disabled}
                                        />
                                    </div>
                                </div>

                                {leaveBalance && !leaveBalancesUpdating ? <>
                                    <div className='col-xs-8'>
                                        <div className="col-xs-6">
                                            <div className='form-material'>
                                                <label>From</label>
                                                <ReactDatePicker
                                                    disabled={disabled}
                                                    dateFormat="dd MMM yyyy"
                                                    customInput={<DateInputFormat />}
                                                    minDate={!applyIfBalanceAvailable && childDOB ? stringToDate(childDOB) : lastPaidDate >= dateToString(minDate) ? stringToDate(lastPaidDate): minDate}
                                                    maxDate={applyIfBalanceAvailable ? maxDate : consumeBy ? stringToDate(leaveBalance.consumeBy) : stringToDate(leaveBalance.entitledLatest)}
                                                    selected={leave.date ? stringToDate(leave.date) : null}
                                                    autoComplete="off"
                                                    onChange={(date,) => {
                                                        updateField('date', dateToString(date))
                                                    }}
                                                    onChangeRaw={e => handleRawDate(e.target.value, (date) => {
                                                        updateField('date', dateToString(date))
                                                    })}
                                                    strictParsing
                                                />
                                            </div>
                                        </div>
                                        <div className="col-xs-6">
                                            <div className='form-material'>
                                                <label>To</label>
                                                <ReactDatePicker
                                                    disabled={disabled}
                                                    dateFormat="dd MMM yyyy"
                                                    customInput={<DateInputFormat />}
                                                    minDate={applyIfBalanceAvailable ? stringToDate(entitledLatest) : (leave.date ? stringToDate(leave.date) : minDate)}
                                                    // maxDate={applyIfBalanceAvailable ? maxDate : stringToDate(entitledTotal || entitledPaid)}
                                                    selected={leave.dateTo ? stringToDate(leave.dateTo) : null}
                                                    autoComplete="off"
                                                    onChange={(dateTo,) => {
                                                        updateField('dateTo', dateToString(dateTo))
                                                    }}
                                                    onChangeRaw={e => handleRawDate(e.target.value, (dateTo) => {
                                                        updateField('dateTo', dateToString(dateTo))
                                                    })}
                                                    strictParsing
                                                />
                                            </div>
                                        </div>
                                        {notifiedBefore ?
                                            <div className="col-xs-6">
                                                <div className='form-material'>
                                                    <label>Notified On</label>
                                                    <ReactDatePicker
                                                        disabled={disabled}
                                                        dateFormat="dd MMM yyyy"
                                                        customInput={<DateInputFormat />}
                                                        minDate={minDate}
                                                        maxDate={maxDate}
                                                        selected={leave.dateNotified ? stringToDate(leave.dateNotified) : null}
                                                        autoComplete="off"
                                                        onChange={(dateNotified,) => {
                                                            updateField('dateNotified', dateToString(dateNotified))
                                                        }}
                                                        onChangeRaw={e => handleRawDate(e.target.value, (dateNotified) => {
                                                            updateField('dateNotified', dateToString(dateNotified))
                                                        })}
                                                        strictParsing
                                                    />
                                                </div>
                                            </div>
                                            : null}
                                        {revokeAfter ?
                                            <div className="col-xs-6">
                                                <div className='form-material'>
                                                    <label>Revoked On</label>
                                                    <ReactDatePicker
                                                        disabled={disabled}
                                                        dateFormat="dd MMM yyyy"
                                                        customInput={<DateInputFormat />}
                                                        minDate={leave.date ? stringToDate(leave.date) : minDate}
                                                        maxDate={stringToDate(revokeAfter)}
                                                        selected={leave.dateRevoked ? stringToDate(leave.dateRevoked) : null}
                                                        autoComplete="off"
                                                        onChange={(dateRevoked,) => {
                                                            updateField('dateRevoked', dateToString(dateRevoked))
                                                        }}
                                                        onChangeRaw={e => handleRawDate(e.target.value, (dateRevoked) => {
                                                            updateField('dateRevoked', dateToString(dateRevoked))
                                                        })}
                                                        strictParsing
                                                    />
                                                </div>
                                            </div>
                                            : null}
                                    </div>
                                    <div className="col-xs-4">
                                        <div className="col-xs-12 pl-0 pr-0">
                                            <div className='form-material'>
                                                <label>Attachments</label>
                                                <div className='btn btn-success'>
                                                    <ReactFileReader fileTypes={[".jpg", ".pdf", ".png", ".jpeg"]} handleFiles={handleFileUpload}>
                                                        <ImagePlusIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
                                                        <div style={{ float: "left" }}>Add Attachment</div>
                                                    </ReactFileReader>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-xs-12 pl-0 pr-0">
                                            <ul style={{ listStyle: 'none' }}>
                                                {uploading?.length > 0 ? uploading.map(fileName =>
                                                    <li>
                                                        <Spinner
                                                            style={{ height: "1.5rem", verticalAlign: 'inherit', marginRight: 5 }}
                                                            className="animate-spin -ml-1 mr-3 h-5 w-5 "
                                                        />
                                                        {fileName}
                                                    </li>
                                                ) : null}
                                                {uploaded?.length ? uploaded.map(file =>
                                                    <li>
                                                        <PhotoIcon style={{ height: "1.5rem", verticalAlign: 'inherit', marginRight: 5 }} />
                                                        {file.name}
                                                    </li>
                                                ) : null}
                                            </ul>
                                        </div>
                                    </div>
                                    <div className='col-xs-8 form-container'>
                                        <LeaveBreakdown type={"week"} />
                                    </div>
                                    <div className='col-xs-12 form-container'>
                                        <div className="col-xs-4">
                                            <label>Weeks</label>
                                            <input
                                                className="form-control form-input"
                                                name="weeks"
                                                value={leave?.weeklyBreakdown?.length}
                                                disabled={true}
                                            />
                                            <div className="clearfix" />
                                        </div>

                                        <div className="col-xs-4">
                                            <label htmlFor="material-text">Status</label>
                                            <ReactSelect
                                                value={leaveStatusOptions?.find(item => item.value == leave.status)}
                                                options={leaveStatusOptions}
                                                onChange={(selected) => updateField('status', selected?.value)}
                                                isDisabled={disabled}
                                            />
                                        </div>
                                    </div>
                                    <div className='col-xs-12 form-container'>
                                        <SubmitButtons />
                                    </div>
                                </> : null}
                                <div className='clearfix' />
                            </div>
                        </div>
                    </Tooltip>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state, ownProps) => {
    const { jurisdiction } = { ...state.company?.settings }
    const { child } = { ...state.employee?.children }
    const { employeeDetails, id: agreementId } = { ...state.employee?.agreementInfo }
    const { leaveBalance, leaveBalancesUpdating } = { ...state.leave }
    const { givenName, familyName } = { ...employeeDetails }

    const [leaveTypeSettings, leaveTypes] = [state.leave?.leaveSettings?.leaveTypes, state.leave?.leaveTypes]
    const leaveTypeOptions = leaveTypes
        ?.map(item => ({ ...item, ...leaveTypeSettings?.find(setting => setting?.id == item?.id) }))
        ?.filter(item => item.parental || item.type == "Parental")
        ?.filter(item => child?.adopted ? (item.id !== '6034') : (item.id !== '7093f4ba-27ce-52ee-a738-8f8399b0a396'))
        ?.map(leaveType => ({
            value: leaveType.id,
            label: leaveType.name,
            applyIfBalanceAvailable: !!leaveType.applyIfBalanceAvailable,
        }))

    const employeeName = `${givenName} ${familyName}`
    const { lastPaidDate, isPaid: modalIsPaid } = { ...state.leave?.modalStatus }

    const leaveStatusOptions = [
        {
            value: "Approved",
            label: "Approved",
        },
        {
            value: "Pending",
            label: "Pending",
        },
        {
            value: "Cancelled",
            label: "Cancelled",
        },
        {
            value: "Denied",
            label: "Denied",
        },
        {
            value: "Deleted",
            label: "Deleted",
        },
        {
            value: "Pending Cancellation",
            label: "Pending Cancellation",
        }
    ]
    return {
        jurisdiction,
        child,
        leave: state.leave.modalLeave || {},
        modalOpen: state.employee?.children?.childLeaveModal,
        leaveTypeOptions,
        leaveStatusOptions,
        leaveBalancesUpdating,
        employeeName,
        agreementId,
        leaveBalance,
        modalIsPaid,
        uploading: state.leave.filesUploading,
        uploaded: state.leave.filesUploaded,
        saving: state.employee?.children?.saving,
        lastPaidDate,
        jurisdiction: state.company?.settings?.jurisdiction,
        disabled: (state?.permissions?.my?.permissions?.people == "Read"),
    };
};

const mapDispatchToProps = (dispatch, ownProps) => {
    return {
        updateField: (field, newValue) => dispatch(LEAVE_ACTIONS.modalUpdateField(field, newValue)),
        closeModal: () => dispatch(EMPLOYEE_ACTIONS.toggleChildLeaveModal({ open: false })),
        saveItem: (leave) => dispatch(LEAVE_ACTIONS.saveLeave(leave)),
        getChildRelatedBalance: ({ agreementId, childId, type, date }) => dispatch(LEAVE_ACTIONS.getChildRelatedBalance({ agreement: agreementId, child: childId, type, date })),
        getLeaveTypes: () => dispatch(LEAVE_ACTIONS.getLeaveTypes()),
        mapBreakdown: () => dispatch(LEAVE_ACTIONS.mapBreakdown()),
        uploadFile: (file, agreement) => dispatch(LEAVE_ACTIONS.uploadFile({ file, agreement, uploadType: 'LEAVE_MODAL' })),
        getLeaveBreakdown: (id) => dispatch(LEAVE_ACTIONS.leaveGetBreakdown(id)),

    }
};

export default connect(mapStateToProps, mapDispatchToProps)(ChildLeaveModal);
